import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as AddBookingButtonIcon } from '../../assets/icon/add-booking-room.svg';
import { fetchBookings } from '../../state/actions/booking';
import AddBookingModal from './AddBookingModal';
import moment from 'moment-timezone';
import './VirtualCalendar.css';

const SGT_TIMEZONE = 'Asia/Singapore';
const HOUR_HEIGHT = 60; // Height of each hour in pixels
const START_HOUR = 6; // Start at 6 AM
const END_HOUR = 24; // End at 12 AM (24 hours)
const TOTAL_HOURS = END_HOUR - START_HOUR;
const TOTAL_HEIGHT = TOTAL_HOURS * HOUR_HEIGHT;
const VIEWPORT_HEIGHT = 600; // Fixed viewport height

const VirtualCalendarWeekly = ({ dateRange, search = '', selectedRooms =[],selectedRoom = 'all', viewMode = 'weekly', siteId, rooms: roomsFromProps = [] }) => {
  const dispatch = useDispatch();
  const { bookings, loading, activeTab } = useSelector((state) => ({
    bookings: state.booking?.bookings || [],
    loading: state.booking?.loading || false,
    activeTab: state.tab?.tab,
  }));

  const [scrollTop, setScrollTop] = useState(0);
  const [currentTime, setCurrentTime] = useState(moment().tz(SGT_TIMEZONE));
  const [isBookingModalVisible, setIsBookingModalVisible] = useState(false);
  const [selectedBookingSlot, setSelectedBookingSlot] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [clickedBookingId, setClickedBookingId] = useState(null);

  const scrollContainerRef = useRef();

  // Generate week days from dateRange (Sunday to Saturday)
  const weekDays = useMemo(() => {
    if (!dateRange?.start) return [];

    const startDate = moment(dateRange.start);
    // Find the Sunday of the week containing the start date
    const weekStart = startDate.clone().startOf('week'); // This gives Sunday
    const days = [];

    for (let i = 0; i < 7; i++) {
      const day = weekStart.clone().add(i, 'days');
      days.push({
        date: day.format('YYYY-MM-DD'),
        dayName: day.format('dddd').toUpperCase(),
        dayNumber: day.format('D'),
        monthName: day.format('MMM'),
        year: day.format('YYYY'),
        isToday: day.isSame(moment(), 'day'),
      });
    }

    return days;
  }, [dateRange]);

  // Generate room colors
  const getRoomColor = (roomId) => {
    const colors = [
      '#8e44ad', // Purple
      '#e74c3c', // Red
      '#f39c12', // Orange
      '#27ae60', // Green
      '#3498db', // Blue
      '#e67e22', // Dark Orange
      '#9b59b6', // Light Purple
      '#1abc9c', // Teal
    ];
    const roomIndex = roomsFromProps.findIndex(r => r._id === roomId);
    return colors[roomIndex % colors.length];
  };

  // Generate lighter background colors
  const getRoomBackgroundColor = (roomId) => {
    const lightColors = [
      '#f4f1f8', // Light Purple
      '#fdf2f2', // Light Red
      '#fef9f0', // Light Orange
      '#f0f8f4', // Light Green
      '#f0f8ff', // Light Blue
      '#fef7f0', // Light Dark Orange
      '#f8f4f9', // Light Light Purple
      '#f0fdfc', // Light Teal
    ];
    const roomIndex = roomsFromProps.findIndex(r => r._id === roomId);
    return lightColors[roomIndex % lightColors.length];
  };

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(moment().tz(SGT_TIMEZONE));
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  console.log(selectedRoom)

  // Fetch bookings when component mounts or dependencies change
  useEffect(() => {
    if (!dateRange?.start || !dateRange?.end) return;

    if (activeTab === 'dashboard' || activeTab === 'roomManagement') {
      dispatch(
        fetchBookings({
          startDate: dateRange.start,
          endDate: dateRange.end,
          search: search,
          roomId: selectedRooms,
          type: viewMode,
          siteId: siteId,
        })
      );
    }
  }, [dispatch, dateRange?.start, dateRange?.end, activeTab, search, selectedRoom, viewMode, siteId]);

  // Calculate visible time range based on scroll position
  const visibleTimeRange = useMemo(() => {
    const startHourOffset = Math.floor(scrollTop / HOUR_HEIGHT);
    const endHourOffset = Math.ceil(
      (scrollTop + VIEWPORT_HEIGHT) / HOUR_HEIGHT
    );

    const startHour = Math.max(START_HOUR, START_HOUR + startHourOffset - 1);
    const endHour = Math.min(END_HOUR, START_HOUR + endHourOffset + 1);

    return { startHour, endHour };
  }, [scrollTop]);

  // Get current time indicator position
  const getCurrentTimePosition = () => {
    const hour = currentTime.hour() + currentTime.minute() / 60;
    if (hour < START_HOUR || hour > END_HOUR) return null;

    return (hour - START_HOUR) * HOUR_HEIGHT;
  };

  // Handle scroll events
  const handleScroll = useCallback((e) => {
    const newScrollTop = e.target.scrollTop;
    setScrollTop(newScrollTop);
  }, []);

  // Handle booking slot click
  const handleBookingSlotClick = (dayDate, hour) => {
    setSelectedBookingSlot({
      date: dayDate,
      hour,
      roomId: selectedRooms,
    });
    setEditingBooking(null);
    setIsBookingModalVisible(true);
  };

  // Handle booking event click (edit mode)
  const handleBookingEventClick = (booking) => {
    setEditingBooking(booking);
    setSelectedBookingSlot(null);
    setClickedBookingId(booking._id);
    setIsBookingModalVisible(true);
  };

  // Handle modal close
  const handleBookingModalCancel = () => {
    setIsBookingModalVisible(false);
    setSelectedBookingSlot(null);
    setEditingBooking(null);
    setClickedBookingId(null);
  };

  // Handle booking creation success
  const handleBookingSubmit = () => {
    // Refresh bookings after creating/updating booking
    if (dateRange?.start && dateRange?.end && (activeTab === 'dashboard' || activeTab === 'roomDetail')) {
      dispatch(
        fetchBookings({
          startDate: dateRange.start,
          endDate: dateRange.end,
          search: search,
          roomId: selectedRooms,
          type: viewMode,
          siteId: siteId,
        })
      );
    }
    setClickedBookingId(null);
  };

  // Generate time slots
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = START_HOUR; hour < END_HOUR; hour++) {
      const time = moment().hour(hour).minute(0);
      slots.push({
        hour,
        time: time.format('h:mm A'),
        position: (hour - START_HOUR) * HOUR_HEIGHT,
      });
    }
    return slots;
  }, []);

  // Group bookings by day - only store unique bookings per day
  const groupedBookings = useMemo(() => {
    const grouped = {};

    bookings.forEach(booking => {
      const bookingDate = moment(booking.startTimeSGT, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD');

      if (!grouped[bookingDate]) {
        grouped[bookingDate] = [];
      }

      // Only add if not already in this day (avoid duplicates from multiple rooms)
      if (!grouped[bookingDate].find(b => b._id === booking._id)) {
        grouped[bookingDate].push(booking);
      }
    });

    return grouped;
  }, [bookings]);

  // Detect overlapping bookings
  const detectOverlappingBookings = (bookings) => {
    const sortedBookings = [...bookings].sort((a, b) => {
      const aStart = moment(a.startTimeSGT, 'YYYY-MM-DD HH:mm:ss');
      const bStart = moment(b.startTimeSGT, 'YYYY-MM-DD HH:mm:ss');
      return aStart.diff(bStart);
    });

    const result = [];
    for (let i = 0; i < sortedBookings.length; i++) {
      const booking = sortedBookings[i];
      const bookingStart = moment(booking.startTimeSGT, 'YYYY-MM-DD HH:mm:ss');
      const bookingEnd = moment(booking.endTimeSGT, 'YYYY-MM-DD HH:mm:ss');

      let column = 0;
      console.log(booking, 'booking')

      // Find the first available column
      while (result.some(item =>
        item.column === column &&
        moment(item.booking.startTimeSGT, 'YYYY-MM-DD HH:mm:ss').isBefore(bookingEnd) &&
        moment(item.booking.endTimeSGT, 'YYYY-MM-DD HH:mm:ss').isAfter(bookingStart)
      )) {
        column++;
      }

      result.push({ booking, column });
    }
    console.log(result, 'result')

    return result;
  };

  // Calculate booking style and position
  const getBookingStyle = (booking, dayIndex, column, totalColumns) => {
    const startTime = moment(booking.startTimeSGT, 'YYYY-MM-DD HH:mm:ss');
    const endTime = moment(booking.endTimeSGT, 'YYYY-MM-DD HH:mm:ss');

    const startHour = startTime.hour() + startTime.minute() / 60;
    const endHour = endTime.hour() + endTime.minute() / 60;

    const top = (startHour - START_HOUR) * HOUR_HEIGHT;
    const height = Math.max((endHour - startHour) * HOUR_HEIGHT, 30);
    const dayWidth = `calc((100% - 80px) / 7)`;
    const columnWidth = totalColumns > 1 ? `calc(${dayWidth} / ${totalColumns})` : dayWidth;
    const left = `calc(80px + ${dayIndex} * ${dayWidth} + ${column} * (${dayWidth} / ${totalColumns}))`;

    return {
      position: 'absolute',
      top: `${top}px`,
      height: `${height}px`,
      left: left,
      width: `calc(${columnWidth} - 4px)`, // Small margin
      zIndex: clickedBookingId === booking._id ? 1000 : 20 + column,
      transform: 'translateZ(0)',
    };
  };

  const currentTimePosition = getCurrentTimePosition();

  return (
    <div className="virtual-calendar">
      {loading && <div className="calendar-loading">Loading...</div>}

      {/* Calendar Header */}
      <div className="calendar-header">
        <div className="time-column-header">UTC+08</div>
        {weekDays.map((day, dayIndex) => {
          const dayWidth = `calc((100% - 80px) / 7)`;
          return (
            <div
              key={day.date}
              className="room-header-weekly"
              style={{
                width: dayWidth,
                flex: 'none'
              }}
            >
              <div className="room-name">
                {day.dayName}, <br/> {day.dayNumber} {day.monthName} {day.year}
              </div>
            </div>
          );
        })}
      </div>

      {/* Scrollable Calendar Body */}
      <div
        ref={scrollContainerRef}
        className="calendar-body"
        style={{ height: VIEWPORT_HEIGHT }}
        onScroll={handleScroll}
      >
        <div className="calendar-content" style={{ height: TOTAL_HEIGHT }}>
          {/* Time Grid Lines */}
          {timeSlots.map((slot) => (
            <div
              key={slot.hour}
              className="time-slot"
              style={{ top: slot.position }}
            >
              <div className="time-label">{slot.time}</div>
              <div
                className="time-line"
                style={{
                  width: 'calc(100% - 80px)',
                  left: '80px',
                  position: 'absolute',
                  borderTop: '1px solid #e8e8e8',
                  top: 0
                }}
              />
            </div>
          ))}

          {/* Current Time Indicator */}
          {currentTimePosition !== null && (
            <div
              className="current-time-line"
              style={{ top: `${currentTimePosition}px` }}
            />
          )}

          {/* Day Columns */}
          {weekDays.map((day, dayIndex) => {
            const dayWidth = `calc((100% - 80px) / 7)`;
            const dayLeft = `calc(80px + ${dayIndex} * ${dayWidth})`;

            return (
              <div
                key={day.date}
                className="room-column"
                style={{
                  left: dayLeft,
                  width: dayWidth,
                  height: TOTAL_HEIGHT,
                }}
              >
                {/* Add Booking Buttons */}
                {timeSlots.map((slot) => (
                  <div
                    key={`${day.date}-${slot.hour}`}
                    className="add-booking-slot"
                    style={{ top: slot.position, height: HOUR_HEIGHT }}
                    onClick={() => handleBookingSlotClick(day.date, slot.hour)}
                  >
                    <AddBookingButtonIcon style={{ cursor: 'pointer' }}/>
                  </div>
                ))}
              </div>
            );
          })}

          {/* Booking Events */}
          {weekDays.map((day, dayIndex) => {
            const dayBookings = groupedBookings[day.date] || [];
            const arrangedBookings = detectOverlappingBookings(dayBookings);
            const totalColumns = arrangedBookings.length > 0 ? Math.max(...arrangedBookings.map(item => item.column)) + 1 : 1;

            console.log(arrangedBookings, totalColumns, 'arranged bookings total columns')
            return (
              <div key={`bookings-${day.date}`}>
                {arrangedBookings.map((item) => {
                  const { booking, column } = item;
                  const roomColor = booking.roomIds?.[0] ? getRoomColor(booking.roomIds[0]._id) : '#8e44ad';
                  const roomBackgroundColor = booking.roomIds?.[0] ? getRoomBackgroundColor(booking.roomIds[0]._id) : '#f4f1f8';

                  const eventStyle = {
                    ...getBookingStyle(booking, dayIndex, column, totalColumns),
                    borderLeftColor: roomColor,
                    backgroundColor: roomBackgroundColor,
                    cursor: 'pointer',
                  };

                  return (
                    <div
                      key={`${booking._id}-${dayIndex}`}
                      className="booking-event"
                      style={eventStyle}
                      onClick={() => handleBookingEventClick(booking)}
                    >
                      <div className="booking-title">{booking.title}</div>
                      <div className="booking-person">
                        Booked By {booking.picName}
                      </div>
                      <div className="booking-time">
                        {moment(booking.startTimeSGT, 'YYYY-MM-DD HH:mm:ss').format('h:mm A')} - {moment(booking.endTimeSGT, 'YYYY-MM-DD HH:mm:ss').format('h:mm A')}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Booking Modal */}
      <AddBookingModal
        isVisible={isBookingModalVisible}
        onCancel={handleBookingModalCancel}
        onSubmit={handleBookingSubmit}
        selectedRoom={selectedBookingSlot?.roomId}
        selectedDate={selectedBookingSlot?.date}
        selectedHour={selectedBookingSlot?.hour}
        rooms={roomsFromProps}
        editingBooking={editingBooking}
      />
    </div>
  );
};

export default VirtualCalendarWeekly;
